/**
 * Sidebar descriptor builders derived from the module registry + access policy.
 *
 * Returns plain navigation objects (`to`, `Icon`, `label`, …). Rendering and
 * `navigate()` calls stay in {@link ../components/Sidebar/Sidebar.jsx}.
 *
 * @module modules/appModuleNavigation
 */

import { getModuleCatalogEntries } from "./appModuleCatalog";
import { getModuleIcon } from "./appModuleIcons";
import { canAccessModule, doesPathMatchRoutePattern } from "./appModuleAccess";

/**
 * @param {Function|undefined} t — i18next `t` from the `common` namespace
 * @param {string} labelKey — registry `sidebar.labelKey`
 * @param {string} fallback — registry `label` when `t` is unavailable
 */
function translateLabel(t, labelKey, fallback) {
  if (typeof t !== "function") return fallback;
  return t(labelKey, { defaultValue: fallback });
}

/** Whether the current URL should highlight a sidebar item for this module. */
function isRouteActive(moduleConfig, pathname) {
  const matchPaths = moduleConfig.sidebar.matchPaths.length > 0
    ? moduleConfig.sidebar.matchPaths
    : moduleConfig.routePaths;

  return matchPaths.some((routePath) => doesPathMatchRoutePattern(pathname, routePath));
}

/**
 * @typedef {Object} SidebarNavDescriptor
 * @property {string} key — module key
 * @property {string} label — translated label
 * @property {string} to — `defaultPath` for navigation
 * @property {React.ComponentType} Icon — icon component ref from registry
 * @property {string} [className]
 * @property {boolean} isActive
 * @property {number} order
 */

/**
 * Build sidebar items for a given placement (`nav` | `control`).
 * Filters by {@link canAccessModule} — no env-var or inline role checks here.
 *
 * @param {"nav"|"control"|"none"} placement — registry `sidebar.placement`
 * @param {Object|undefined} userData
 * @param {Object} [options]
 * @param {Function} [options.t]
 * @param {string} [options.pathname] — current location for active state
 * @returns {SidebarNavDescriptor[]}
 */
function getSidebarItemsForPlacement(placement, userData, { t, pathname } = {}) {
  return getModuleCatalogEntries()
    .filter((moduleConfig) => moduleConfig.sidebar.placement === placement)
    .filter((moduleConfig) => canAccessModule(moduleConfig.key, userData))
    .sort((left, right) => left.sidebar.order - right.sidebar.order)
    .map((moduleConfig) => ({
      key: moduleConfig.key,
      label: translateLabel(t, moduleConfig.labelKey, moduleConfig.label),
      to: moduleConfig.defaultPath,
      Icon: getModuleIcon(moduleConfig.sidebar.iconName),
      className: moduleConfig.sidebar.className,
      isActive: isRouteActive(moduleConfig, pathname),
      order: moduleConfig.sidebar.order,
    }));
}

/**
 * Main sidebar module cluster (Obligations, Files, Actions, …).
 *
 * @param {Object|undefined} userData
 * @param {Object} [options]
 * @param {Function} [options.t]
 * @param {string} [options.pathname]
 * @returns {SidebarNavDescriptor[]}
 */
export function getSidebarNavItems(userData, options = {}) {
  return getSidebarItemsForPlacement("nav", userData, options);
}

/**
 * Sidebar control row (Dashboard home icon, Calendar, …).
 *
 * @param {Object|undefined} userData
 * @param {Object} [options]
 * @param {Function} [options.t]
 * @param {string} [options.pathname]
 * @returns {SidebarNavDescriptor[]}
 */
export function getSidebarControlItems(userData, options = {}) {
  return getSidebarItemsForPlacement("control", userData, options);
}
